import React, { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller, useFormContext } from 'react-hook-form';

import {
  Box,
  Button,
  Dialog,
  TextField,
  DialogTitle,
  Autocomplete,
  DialogContent,
} from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';

import { Form } from 'src/components/hook-form';

import { useGetChannelListMutation } from '../channel/slice/channelApi';

// Types
type Props = {
  open: boolean;
  onClose: () => void;
  handleSubmitData: (data: any) => void;
};

type CreateTemplateSchemaType = {
  Channel: string;
};
function SyncTamplateForm({ open, onClose, handleSubmitData }: Props) {
  const [getChannelList] = useGetChannelListMutation();
  const [_channelList, setChannelList] = useState<any>([]);

  const { t } = useTranslate('translation');

  const defaultValues: CreateTemplateSchemaType = {
    Channel: '',
  };

  useEffect(() => {
    if (open) {
      const defaultFilter = {
        filters: [{ fieldname: 'channelType', search: 'WHATSAPP' }],
        globalSearch: '',
        limit: 20,
        page: 1,
      };
      getChannelList(defaultFilter)
        .unwrap()
        .then((res) => {
          setChannelList(res.list);
        })
        .catch(() => {});
    } else {
      setChannelList([]); // Clear channel list when dialog is closed
    }

    return () => {
      setChannelList([]); // Clear channel list on unmount
    };
  }, [open]);

  const fldList = [{ name: 'Channel', required: true }];

  const ContactSchema = validationSchema(fldList, t);

  const methods = useForm<CreateTemplateSchemaType>({
    mode: 'all',
    resolver: zodResolver(ContactSchema),
    values: defaultValues,
  });
  const { handleSubmit, reset, setValue } = methods;

  useEffect(() => {
    if (_channelList.length > 0) {
      setValue('Channel', _channelList[0].id); // set default  select value
    }
  }, [_channelList]);

  const onSubmit = handleSubmit((data: CreateTemplateSchemaType) => {
    handleSubmitData(data);
  });

  const onCloseHandler = () => {
    onClose();
    reset();
    setChannelList([]);
  };
  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onCloseHandler} sx={{ p: 0.5 }}>
      <Box>
        <DialogTitle sx={{ p: 3 }}>{`${t('SELECT_CHANNEL_FOR_SYNC')}`}</DialogTitle>

        <DialogContent dividers sx={{ pt: 1, pb: 0, border: 'none' }}>
          <Form methods={methods} onSubmit={onSubmit}>
            <Box sx={{ my: 1 }}>
              <ChannelAutoComplete name="Channel" options={_channelList} />

              {/* <Field.Select
                name="Channel"
                label={t('SELECT_CHANNEL')}
                slotProps={{ inputLabel: { shrink: true } }}
              >
                {_channelList.map((option: any) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.channelName}
                  </MenuItem>
                ))}
              </Field.Select> */}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'end', my: 4 }}>
              <Button variant="outlined" color="inherit" onClick={onCloseHandler}>
                {t('CANCEL')}
              </Button>

              <Button type="submit" variant="contained" sx={{ ml: 2 }}>
                {t('SYNC')}
              </Button>
            </Box>

            {/* {channelMenu.value && renderSelectRecordDialog()} */}
          </Form>
        </DialogContent>
      </Box>
    </Dialog>
  );
}

export default SyncTamplateForm;

export const ChannelAutoComplete = ({
  name,
  options,
  label = 'Select Channel',
  onChangeField,
}: any) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          fullWidth
          options={options}
          getOptionLabel={(option) => option.channelName || option.label || ''}
          value={
            options.find((opt: any) => opt.id === field.value || opt.value === field.value) || null
          }
          onChange={(_, selectedOption) => {
            field.onChange(selectedOption ? selectedOption.id || selectedOption.value || '' : null);
            setValue(name, selectedOption ? selectedOption.id || selectedOption.value || '' : '');
            if (onChangeField) onChangeField({ fieldName: name, value: [selectedOption.value] });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              error={!!error} // show red border if error
              helperText={error?.message}
            />
          )}
        />
      )}
    />
  );
};
